export type PwaInstallState = "available" | "installed" | "manual";

export interface PwaInstallResult {
  outcome: "accepted" | "dismissed" | "installed" | "manual";
  message: string;
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const listeners = new Set<(state: PwaInstallState) => void>();
let deferredPrompt: BeforeInstallPromptEvent | null = null;

function runsStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

let state: PwaInstallState = runsStandalone() ? "installed" : "manual";

function setState(next: PwaInstallState): void {
  state = next;
  for (const listener of listeners) listener(state);
}

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferredPrompt = event as BeforeInstallPromptEvent;
  setState("available");
});

window.addEventListener("appinstalled", () => {
  deferredPrompt = null;
  setState("installed");
});

export function subscribeInstallState(
  listener: (state: PwaInstallState) => void,
): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

function manualInstallMessage(): string {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
    return "In Safari, tap Share, then Add to Home Screen.";
  }
  return "Use your browser’s Install app or Add to Home Screen command to install Klint.";
}

export async function requestPwaInstall(): Promise<PwaInstallResult> {
  if (runsStandalone() || state === "installed") {
    setState("installed");
    return { outcome: "installed", message: "Klint is already installed." };
  }

  const prompt = deferredPrompt;
  if (!prompt) return { outcome: "manual", message: manualInstallMessage() };

  await prompt.prompt();
  const choice = await prompt.userChoice;
  deferredPrompt = null;
  setState(choice.outcome === "accepted" ? "installed" : "manual");
  return choice.outcome === "accepted"
    ? { outcome: "accepted", message: "Klint has been added as an app." }
    : { outcome: "dismissed", message: "Installation cancelled." };
}
